import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAgentSession } from '../../contexts/AgentSessionContext';
import {
  TrendingUp,
  LogOut,
  Database,
  Zap,
  RefreshCw,
  History,
  Cloud,
  CloudUpload,
  AlertCircle,
  Key,
  ExternalLink,
  Menu,
  X,
} from 'lucide-react';
import { ApiKeyModal } from './ApiKeyModal';
import { getEffectiveApiKey } from '../../utils/backendHeaders';

interface NavbarProps {
  onToggleInspector: () => void;
  inspectorOpen: boolean;
  onToggleHistory: () => void;
  historyOpen: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({
  onToggleInspector,
  inspectorOpen,
  onToggleHistory,
  historyOpen,
}) => {
  const { userProfile, signOutUser, devToggleApproval } = useAuth();
  const {
    clearChat,
    isStreaming,
    targetAgentId,
    setTargetAgentId,
    threads,
    syncStatus,
    syncNow,
    idleMinutes,
  } = useAgentSession();
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const enableDevToggle = import.meta.env.VITE_ENABLE_DEV_STATUS_TOGGLE === 'true';
  const backendEndpoint =
    import.meta.env.VITE_BACKEND_AGENT_ENDPOINT || 'https://finadv-agent-api.adhyaconsulting.org';
  const hasApiKey = Boolean(getEffectiveApiKey());

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  return (
    <header className="navbar">
      <div className="navbar-left">
        <div className="navbar-logo">
          <TrendingUp className="logo-icon" size={22} />
          <span className="logo-title">Finance<span className="accent">Advisor</span></span>
          <span className="badge-adk hide-on-mobile">Google ADK</span>
        </div>

        <div className="agent-selector-container">
          <select
            value={targetAgentId}
            onChange={(e) => {
              setTargetAgentId(e.target.value);
              clearChat();
            }}
            disabled={isStreaming}
            className="agent-version-select"
            title="Target Google ADK Agent Version"
            aria-label="Target Google ADK Agent Version"
          >
            <option value="finance_advisor_v14">V14 (Production Gateway)</option>
            <option value="finance_advisor_v13">V13 (MCP Server Design)</option>
            <option value="finance_advisor_v12">V12 (Structured Contract)</option>
            <option value="social_poster">Social Spark (Multi-Agent)</option>
          </select>
        </div>
      </div>

      <div className="navbar-right">
        {/* Desktop-only Direct Links */}
        <a
          href="/landing/"
          target="_blank"
          rel="noopener noreferrer"
          className="nav-btn btn-landing-link hide-on-mobile"
          title="Product Landing Page, Video Walkthrough & Pitch Deck"
          aria-label="Open Product Landing Page and Video Walkthrough"
          style={{ textDecoration: 'none' }}
        >
          <ExternalLink size={14} className="text-blue" />
          <span className="hide-mobile">Landing & Demos</span>
        </a>

        {enableDevToggle && (
          <button
            onClick={devToggleApproval}
            className="nav-btn btn-dev-small hide-on-mobile"
            title="Dev Toggle: Toggle between APPROVED and NEW"
          >
            <Zap size={14} />
            <span className="hide-mobile">Dev Status</span>
          </button>
        )}

        {/* Sync Status Button (Always visible) */}
        <button
          onClick={syncNow}
          className={`nav-btn btn-sync-status status-${syncStatus}`}
          title={
            syncStatus === 'syncing'
              ? 'Saving chat session to Firebase...'
              : syncStatus === 'pending'
              ? `Session has unsaved changes. Will auto-sync to Firebase after ${idleMinutes}m of inactivity (or click to sync now).`
              : syncStatus === 'error'
              ? 'Failed to sync to Firebase. Click to retry.'
              : 'Chat session is synced to Firebase.'
          }
          aria-label="Sync session to Firebase"
        >
          {syncStatus === 'syncing' ? (
            <RefreshCw size={13} className="spin text-blue" />
          ) : syncStatus === 'pending' ? (
            <CloudUpload size={13} className="text-yellow" />
          ) : syncStatus === 'error' ? (
            <AlertCircle size={13} className="text-red" />
          ) : (
            <Cloud size={13} className="text-green" />
          )}
          <span className="hide-mobile">
            {syncStatus === 'syncing'
              ? 'Syncing...'
              : syncStatus === 'pending'
              ? 'Unsaved'
              : syncStatus === 'error'
              ? 'Sync Error'
              : 'Synced'}
          </span>
        </button>

        {/* New Session Button (desktop; on mobile it lives in the overflow sheet and history drawer) */}
        <button
          onClick={clearChat}
          disabled={isStreaming}
          className="nav-btn btn-clear hide-on-mobile"
          title="End session & start fresh thread (flushes unsaved chats to Firebase)"
          aria-label="Start new session"
        >
          <RefreshCw size={14} className={isStreaming ? 'spin' : ''} />
          <span className="hide-mobile">New Session</span>
        </button>

        {/* History Drawer Toggle (Always visible) */}
        <button
          onClick={onToggleHistory}
          className={`nav-btn btn-history ${historyOpen ? 'active' : ''}`}
          title="Toggle Chat History Drawer"
          aria-label="Toggle Chat History Drawer"
          aria-expanded={historyOpen}
        >
          <History size={14} />
          <span className="hide-mobile">History</span>
          {threads.length > 0 && (
            <span className="nav-count-badge">{threads.length}</span>
          )}
        </button>

        {/* State Memory Drawer Toggle (Always visible) */}
        <button
          onClick={onToggleInspector}
          className={`nav-btn btn-inspector ${inspectorOpen ? 'active' : ''}`}
          title="Toggle State & Holdings Inspector"
          aria-label="Toggle State & Holdings Inspector"
          aria-expanded={inspectorOpen}
        >
          <Database size={14} />
          <span className="hide-mobile">State Memory</span>
        </button>

        {/* Desktop API Key Modal Trigger */}
        <button
          onClick={() => setApiKeyModalOpen(true)}
          className={`nav-btn btn-key hide-on-mobile ${hasApiKey ? 'key-configured' : 'key-missing'}`}
          title={hasApiKey ? 'Backend API Key configured (Click to edit/view)' : 'Configure Backend API Key (X-API-Key)'}
        >
          <Key size={14} className={hasApiKey ? 'text-green' : 'text-yellow'} />
          <span className="hide-mobile">{hasApiKey ? 'API Key' : 'Set Key'}</span>
        </button>

        {/* Desktop Profile Badge */}
        <div className="user-profile-badge hide-on-mobile">
          {userProfile?.photoURL ? (
            <img
              src={userProfile.photoURL}
              alt={userProfile.displayName || 'User'}
              className="navbar-avatar"
            />
          ) : (
            <div className="navbar-avatar-fallback">
              {userProfile?.displayName ? userProfile.displayName[0].toUpperCase() : 'U'}
            </div>
          )}
          <div className="user-text-info hide-mobile">
            <span className="user-display-name">{userProfile?.displayName || 'User'}</span>
            <span className="user-status-pill status-approved">
              {userProfile?.user_status}
            </span>
          </div>
        </div>

        {/* Desktop Logout Button */}
        <button onClick={signOutUser} className="nav-btn btn-logout hide-on-mobile" title="Sign Out" aria-label="Sign Out">
          <LogOut size={16} />
        </button>

        {/* Mobile Hamburger / Overflow Menu Toggle */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`nav-btn btn-mobile-menu show-on-mobile ${mobileMenuOpen ? 'active' : ''}`}
          title="More options"
          aria-label="Toggle mobile navigation menu"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {/* Mobile Menu Action Sheet */}
      {mobileMenuOpen && (
        <div className="mobile-nav-backdrop" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="mobile-nav-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Account and session options"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-user-header">
              {userProfile?.photoURL ? (
                <img src={userProfile.photoURL} alt={userProfile.displayName || 'User'} className="mobile-avatar" />
              ) : (
                <div className="navbar-avatar-fallback mobile-avatar">
                  {userProfile?.displayName ? userProfile.displayName[0].toUpperCase() : 'U'}
                </div>
              )}
              <div className="mobile-user-details">
                <span className="mobile-user-name">{userProfile?.displayName || 'User'}</span>
                <span className="mobile-user-email">{userProfile?.email || ''}</span>
                <span className="user-status-pill status-approved" style={{ width: 'fit-content', marginTop: 4 }}>
                  Status: {userProfile?.user_status}
                </span>
              </div>
            </div>

            <div className="mobile-menu-divider" />

            <div className="mobile-menu-actions">
              <button
                onClick={() => {
                  clearChat();
                  setMobileMenuOpen(false);
                }}
                disabled={isStreaming}
                className="mobile-action-btn"
              >
                <RefreshCw size={16} className="text-accent" />
                <span className="mobile-action-label">New Session</span>
              </button>

              <button
                onClick={() => {
                  setApiKeyModalOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="mobile-action-btn"
              >
                <Key size={16} className={hasApiKey ? 'text-green' : 'text-yellow'} />
                <span className="mobile-action-label">Backend API Key</span>
                <span className={`mobile-action-badge ${hasApiKey ? 'badge-configured' : 'badge-missing'}`}>
                  {hasApiKey ? 'Configured' : 'Missing'}
                </span>
              </button>

              <a
                href="/landing/"
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-action-btn"
                onClick={() => setMobileMenuOpen(false)}
                style={{ textDecoration: 'none' }}
              >
                <ExternalLink size={16} className="text-blue" />
                <span className="mobile-action-label">Landing Page & Demos</span>
              </a>

              {enableDevToggle && (
                <button
                  onClick={() => {
                    devToggleApproval();
                    setMobileMenuOpen(false);
                  }}
                  className="mobile-action-btn"
                >
                  <Zap size={16} className="text-yellow" />
                  <span className="mobile-action-label">Toggle Dev Status</span>
                </button>
              )}

              <button
                onClick={() => {
                  signOutUser();
                  setMobileMenuOpen(false);
                }}
                className="mobile-action-btn text-red"
              >
                <LogOut size={16} />
                <span className="mobile-action-label">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiKeyModal
        isOpen={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
        endpoint={backendEndpoint}
      />
    </header>
  );
};
